/**
 * UI utility functions for consistent user interface patterns across the application
 */

/**
 * Format date for display
 * @param {Date|string} date - Date to format
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date string
 */
export const formatDate = (date, options = {}) => {
  if (!date) return '—';
  
  const {
    includeTime = false,
    locale = 'en-US',
    timeZone = 'UTC'
  } = options;
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) {
    return 'Invalid Date';
  }
  
  const formatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...(includeTime && {
      hour: '2-digit',
      minute: '2-digit'
    })
  };
  
  return dateObj.toLocaleDateString(locale, formatOptions);
};

/**
 * Format date and time for display
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted datetime string
 */
export const formatDateTime = (date) => {
  return formatDate(date, { includeTime: true });
};

/**
 * Format relative time (e.g., "2 hours ago")
 * @param {Date|string} date - Date to format
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (date) => {
  if (!date) return '—';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffInSeconds = Math.floor((now - dateObj) / 1000);
  
  if (diffInSeconds < 60) {
    return 'Just now';
  }
  
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
  }
  
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
  }
  
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  }
  
  return formatDate(date);
};

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size string
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Truncate text to specified length
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @param {string} suffix - Suffix to add when truncated
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength = 50, suffix = '...') => {
  if (!text || text.length <= maxLength) {
    return text;
  }
  
  return text.substring(0, maxLength - suffix.length) + suffix;
};

/**
 * Generate initials from name
 * @param {string} name - Full name
 * @returns {string} Initials
 */
export const getInitials = (name) => {
  if (!name) return '';
  
  return name
    .split(' ')
    .map(word => word.charAt(0).toUpperCase())
    .join('')
    .substring(0, 2);
};

/**
 * Generate random color for avatars
 * @param {string} seed - Seed for consistent color generation
 * @returns {string} Hex color code
 */
export const generateAvatarColor = (seed) => {
  const colors = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
    '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9'
  ];
  
  if (!seed) return colors[0];
  
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return colors[Math.abs(hash) % colors.length];
};

/**
 * Check if user has permission for action
 * @param {Object} user - User object
 * @param {string} action - Action to check
 * @param {Object} resource - Resource object (optional)
 * @returns {boolean} True if user has permission
 */
export const hasPermission = (user, action, resource = null) => {
  if (!user || !user.role) return false;
  
  const adminRoles = ['Admin', 'Extension Head', 'GAD', 'Vice Chancellor', 'Vice Chancellor for Research, Development and Extension Services', 'Chancellor'];
  const coordinatorRoles = ['Extension Coordinator'];
  
  // Admin roles can do everything
  if (adminRoles.includes(user.role)) {
    return true;
  }
  
  // Extension Coordinators can manage their own resources
  if (coordinatorRoles.includes(user.role)) {
    if (action === 'edit' || action === 'delete') {
      return resource && resource.created_by === user.id;
    }
    return true;
  }
  
  return false;
};

/**
 * Get user role display name
 * @param {string} role - User role
 * @returns {string} Display name for role
 */
export const getRoleDisplayName = (role) => {
  const roleMap = {
    'Extension Coordinator': 'Extension Coordinator',
    'Extension Head': 'Extension Head',
    'GAD': 'GAD',
    'Vice Chancellor': 'Vice Chancellor',
    'Vice Chancellor for Research, Development and Extension Services': 'VC for RDES',
    'Chancellor': 'Chancellor',
    'Faculty Extensionist': 'Faculty Extensionist',
    'Staff': 'Staff',
    'Dean': 'Dean',
    'Faculty': 'Faculty',
    'Admin': 'Administrator'
  };
  
  return roleMap[role] || role;
};

/**
 * Get user role color
 * @param {string} role - User role
 * @returns {string} Bootstrap color class
 */
export const getRoleColor = (role) => {
  const colorMap = {
    'Admin': 'danger',
    'Chancellor': 'primary',
    'Vice Chancellor': 'info',
    'Vice Chancellor for Research, Development and Extension Services': 'info',
    'Extension Head': 'success',
    'GAD': 'warning',
    'Extension Coordinator': 'secondary',
    'Faculty Extensionist': 'secondary',
    'Staff': 'secondary',
    'Dean': 'secondary',
    'Faculty': 'secondary'
  };
  
  return colorMap[role] || 'secondary';
};

/**
 * Debounce function for search inputs
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Throttle function for scroll events
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function} Throttled function
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};
